import type { DateRange } from 'react-day-picker'

import { format, isValid, parse } from 'date-fns'
import { AlertCircle, Clock, User, Zap } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import type {
  AllLeaveRequestsParams,
  LeaveRequestAdminListItemDto,
} from '@/utils/api/leave-service/models'

import SearchSelect from '@/components/search-select'
import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import { Label } from '@/components/ui/label'
import {
  useAllLeaveRequests,
  useListAllLeaveTypes,
} from '@/utils/api/leave-service'
import { useListDepartments } from '@/utils/api/user-service'
import { formatDate } from '@/utils/DateHelpers'

import { LeaveDetailsDrawer } from '../components/LeaveDetailsDrawer'
import { SubmitLeaveForUserDialog } from '../components/SubmitLeaveForUserDialog'
import { TrackingDialog } from '../components/TrackingDialog'

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  SUBMITTED: 'bg-blue-100 text-blue-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
  CEO_APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-neutral-100 text-neutral-800',
}

export function AdminLeaveRequestsPage() {
  const [page, setPage] = useState(0)
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false)
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false)

  // Filter states
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<
    number | undefined
  >(undefined)
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState<
    number | undefined
  >(undefined)
  const [fromDate, setFromDate] = useState<string | undefined>(undefined)
  const [toDate, setToDate] = useState<string | undefined>(undefined)

  const pageSize = 10

  // Query parameters
  const allLeaveParams: AllLeaveRequestsParams = {
    pageable: {
      page,
      size: pageSize,
      sort: ['currentApproverName,desc'],
    },
    ...(selectedDepartmentId && { departmentId: selectedDepartmentId }),
    ...(selectedLeaveTypeId && { leaveTypeId: selectedLeaveTypeId }),
    ...(fromDate && { from: fromDate }),
    ...(toDate && { to: toDate }),
  }

  // Queries
  const allLeaveQuery = useAllLeaveRequests(allLeaveParams)

  // Fetch departments for filter dropdown
  const departmentsQuery = useListDepartments(
    { page: 0, size: 100 },
    { query: { staleTime: 60000 } },
  )

  // Fetch leave types for filter dropdown
  const leaveTypesQuery = useListAllLeaveTypes(
    { pageable: { page: 0, size: 100 } },
    { query: { staleTime: 60000 } },
  )

  // Get departments and leave types from response data
  const departments = departmentsQuery.data?.data?.content ?? []
  const leaveTypes = leaveTypesQuery.data?.data?.content ?? []

  // Column configuration
  const columns: ColumnConfig<LeaveRequestAdminListItemDto>[] = [
    {
      key: 'applicantName',
      header: 'Applicant',
      cell: (_value, row) => (
        <div className="flex items-center gap-2">
          <User className="size-4 text-muted-foreground" />
          <div>
            <div className="font-medium">{row.applicantName}</div>
            <div className="text-xs text-muted-foreground">
              {row.departmentName}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'leaveTypeName',
      header: 'Leave Type',
      cell: (_value, row) => <Badge variant="outline">{row.leaveTypeName}</Badge>,
    },
    {
      key: 'startDate',
      header: 'Start Date',
      cell: (_value, row) =>
        row.startDate ? formatDate(new Date(row.startDate)) : '—',
    },
    {
      key: 'endDate',
      header: 'End Date',
      cell: (_value, row) =>
        row.endDate ? formatDate(new Date(row.endDate)) : '—',
    },
    {
      key: 'requestedDays',
      header: 'Days',
      cell: (_value, row) => `${row.requestedDays} days`,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (_value, row) => (
        <Badge
          className={
            row.status ? statusColors[row.status] : 'bg-gray-100 text-gray-800'
          }
        >
          {row.status?.replaceAll('_', ' ')}
        </Badge>
      ),
    },
    {
      key: 'pendingSince',
      header: 'Pending Since',
      cell: (_value, row) =>
        row.pendingSince && row.status !== 'APPROVED' && row.status !== 'REJECTED'
          ? formatDate(new Date(row.pendingSince))
          : '—',
    },
    {
      key: 'currentApproverName',
      header: 'Current Approver',
      sortable: true,
      filterable: true,
      cell: (_value, row) =>
        row.currentApproverName && row.status !== 'APPROVED' && row.status !== 'REJECTED'
          ? (
            <div>
              <div className="font-medium text-sm">{row.currentApproverName}</div>
              <div className="text-xs text-muted-foreground">
                {row.currentApproverRoleTitle}
              </div>
            </div>
          )
          : (
            '—'
          ),
    },
    {
      key: 'pendingDurationDays',
      header: 'Pending Days',
      sortable: false,
      filterable: false,
      cell: (_value, row) =>
        row.pendingDurationDays && row.status !== 'APPROVED' && row.status !== 'REJECTED'
          ? (
            <div className="flex items-center gap-1">
              <Clock className="size-3" />
              <span>
                {row.pendingDurationDays}
                {' '}
                days
              </span>
            </div>
          )
          : (
            '—'
          ),
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      filterable: false,
      cell: (_value, row) => (
        <div className="flex items-center gap-2 justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            onClick={() => {
              setSelectedRequestId(row.id!)
              setDrawerOpen(true)
            }}
          >
            View
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-600 hover:text-slate-700 hover:bg-slate-50"
            onClick={() => {
              setSelectedRequestId(row.id!)
              setTrackingDialogOpen(true)
            }}
          >
            Track
          </Button>
        </div>
      ),
    },
  ]

  const isLoading
    = allLeaveQuery.isLoading
      || departmentsQuery.isLoading
      || leaveTypesQuery.isLoading
  const isError = allLeaveQuery.isError
  const data = allLeaveQuery.data?.data?.content ?? []
  const hasActiveFilters
    = selectedDepartmentId !== undefined
      || selectedLeaveTypeId !== undefined
      || Boolean(fromDate)
      || Boolean(toDate)
  const emptyStateMessage = hasActiveFilters
    ? 'No leave requests match the current filters.'
    : 'No leave requests found.'
  const selectedRange = useMemo<DateRange | undefined>(() => {
    const trimmedStart = fromDate?.trim() ?? ''
    const trimmedEnd = toDate?.trim() ?? ''
    const parsedStart = trimmedStart
      ? parse(trimmedStart, 'yyyy-MM-dd', new Date())
      : undefined
    const parsedEnd = trimmedEnd
      ? parse(trimmedEnd, 'yyyy-MM-dd', new Date())
      : undefined
    const startDate = parsedStart && isValid(parsedStart) ? parsedStart : undefined
    const endDate = parsedEnd && isValid(parsedEnd) ? parsedEnd : undefined
    return startDate ? { from: startDate, to: endDate } : undefined
  }, [fromDate, toDate])

  const handleDateRangeSelection = (range: DateRange | undefined) => {
    setFromDate(range?.from ? format(range.from, 'yyyy-MM-dd') : undefined)
    setToDate(range?.to ? format(range.to, 'yyyy-MM-dd') : undefined)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">All Leave Requests</h1>
          <p className="text-muted-foreground mt-1">
            Manage and track all leave requests across the organization
          </p>
        </div>
        <Button
          onClick={() => setSubmitDialogOpen(true)}
          className="gap-2"
        >
          <Zap className="size-4" />
          Submit for User
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card border rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Filter className="size-4 text-muted-foreground" />
          <h3 className="font-medium">Filters</h3>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2">
            <SearchSelect
              label="Department"
              placeholder="All Departments"
              value={selectedDepartmentId?.toString()}
              options={departments.map(dept => ({
                id: typeof dept.id === 'number' || typeof dept.id === 'string' ? dept.id : undefined,
                name: String(dept.name ?? 'Unknown'),
              }))}
              onValueChange={value =>
                setSelectedDepartmentId(value ? Number.parseInt(value, 10) : undefined)}
              isLoading={departmentsQuery.isLoading}
            />
          </div>

          <div className="space-y-2">
            <SearchSelect
              label="Leave Type"
              placeholder="All Leave Types"
              value={selectedLeaveTypeId?.toString()}
              options={leaveTypes.map(type => ({
                id: type.id,
                name: type.name || 'Unknown',
              }))}
              onValueChange={value =>
                setSelectedLeaveTypeId(value ? Number.parseInt(value, 10) : undefined)}
              isLoading={leaveTypesQuery.isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="leave-filter-range">Leave Dates</Label>
            <DateRangePicker
              id="leave-filter-range"
              selected={selectedRange}
              onSelect={handleDateRangeSelection}
              numberOfMonths={2}
              captionLayout="dropdown"
            />
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setSelectedDepartmentId(undefined)
            setSelectedLeaveTypeId(undefined)
            setFromDate(undefined)
            setToDate(undefined)
            setPage(0)
          }}
        >
          Clear Filters
        </Button>
      </div>

      {/* Error State */}
      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load leave requests. Please try again.
          </AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <div className="bg-card border rounded-lg p-4">
        <EnhancedDataTable
          columns={columns}
          data={data}
          loading={isLoading}
          emptyStateMessage={emptyStateMessage}
          paginationCallbacks={{
            onPageChange: setPage,
            totalCount: (allLeaveQuery.data?.data?.totalElements ?? 0),
            manualPagination: true,
          }}
          pageSize={pageSize}
        />
      </div>

      {/* Dialogs */}
      <LeaveDetailsDrawer
        leaveRequestId={selectedRequestId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      <TrackingDialog
        leaveRequestId={selectedRequestId}
        open={trackingDialogOpen}
        onOpenChange={setTrackingDialogOpen}
      />

      <SubmitLeaveForUserDialog
        open={submitDialogOpen}
        onOpenChange={setSubmitDialogOpen}
        onSuccess={() => {
          setSubmitDialogOpen(false)
          allLeaveQuery.refetch()
        }}
      />
    </div>
  )
}

// Simple filter icon component
function Filter({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  )
}
